import { onDisconnect, onValue, ref, serverTimestamp, set } from 'firebase/database';
import { myAuth, myRtdb } from '../contexts/firebaseProvider';

import logger from 'utils/logger';
/**
 * Configura o sistema de presença para um usuário
 * @param {string} userId - ID do usuário autenticado
 * @returns {function} - Função de limpeza para remover listeners
 */
export const setupPresence = (userId) => {
  if (!userId) {
    logger.warn('setupPresence: userId não fornecido');
    return () => { };
  }

  // Verificar se o usuário está autenticado no Firebase Auth
  if (!myAuth.currentUser) {
    logger.warn('setupPresence: usuário não autenticado no Firebase Auth');
    return () => { };
  }

  // Verificar se o UID do Firebase corresponde ao userId fornecido
  if (myAuth.currentUser.uid !== userId) {
    logger.warn('setupPresence: userId não corresponde ao usuário autenticado');
    logger.warn('Firebase UID:', myAuth.currentUser.uid, 'userId fornecido:', userId);
    return () => { };
  }

  logger.log('setupPresence: Configurando presença para usuário:', userId);

  // Referência para o status do usuário no RTDB
  const userStatusRef = ref(myRtdb, `status/${userId}`);

  // Referência para verificar o status da conexão com o Firebase
  const connectedRef = ref(myRtdb, '.info/connected');

  let isActive = true;

  // Listener para monitorar o status da conexão
  const unsubscribe = onValue(connectedRef, (snapshot) => {
    // Verificar novamente se o usuário ainda está autenticado
    if (!myAuth.currentUser || !isActive) {
      logger.warn('setupPresence: usuário não está mais autenticado ou presença foi desativada');
      return;
    }

    if (snapshot.val() === true) {
      logger.log('setupPresence: Conectado ao Firebase RTDB');

      // Quando conectado, configurar o que acontece ao desconectar
      onDisconnect(userStatusRef)
        .set({
          state: 'offline',
          last_seen: serverTimestamp(),
        })
        .then(() => {
          // Verificar novamente se ainda está autenticado antes de definir como online
          if (myAuth.currentUser && isActive) {
            // Após configurar o onDisconnect, definir o usuário como online
            return set(userStatusRef, {
              state: 'online',
              last_seen: serverTimestamp(),
            });
          }
        })
        .then(() => {
          logger.log('setupPresence: Status definido como ONLINE para:', userId);
        })
        .catch((error) => {
          logger.error('Erro ao configurar presença:', error);
          logger.error('Código do erro:', error.code);
          logger.error('Mensagem:', error.message);
        });
    } else {
      logger.log('setupPresence: Desconectado do Firebase RTDB');
    }
  });

  // Retorna função de limpeza
  return () => {
    logger.log('setupPresence: Limpando presença para usuário:', userId);

    // IMPORTANTE: Desativar flag e remover listener PRIMEIRO
    // para evitar que o listener defina como online novamente
    isActive = false;
    unsubscribe();

    // Definir como offline SEMPRE, mesmo se já desautenticou
    // Se não tiver permissão (já desconectou), o erro será ignorado
    // mas o onDisconnect do Firebase vai garantir que fique offline
    set(userStatusRef, {
      state: 'offline',
      last_seen: serverTimestamp(),
    }).catch(() => {
      // Erro esperado se já fez logout (permission denied)
      logger.log('setupPresence: Status offline (via onDisconnect do Firebase)');
    });
  };
};

/**
 * Obtém o status de um usuário específico
 * @param {string} userId - ID do usuário a ser monitorado
 * @param {function} callback - Função callback para receber atualizações de status
 * @returns {function} - Função de limpeza para remover o listener
 */
export const getUserStatus = (userId, callback) => {
  if (!userId) {
    logger.warn('getUserStatus: userId não fornecido');
    return () => { };
  }

  const userStatusRef = ref(myRtdb, `status/${userId}`);

  const unsubscribe = onValue(
    userStatusRef,
    (snapshot) => {
      const status = snapshot.val();
      callback(status || { state: 'offline', last_seen: null });
    },
    (error) => {
      logger.error('Erro ao obter status do usuário:', error);
      logger.error('Detalhes do erro:', {
        code: error.code,
        message: error.message,
        userId: userId
      });
      callback({ state: 'offline', last_seen: null });
    }
  );

  return unsubscribe;
};
